import { gql, useApolloClient, useLazyQuery, useMutation } from '@apollo/client'
import { useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'

import Heading from 'components/v2/Heading'
import FormLayout from 'features/App/components/FormLayout'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { NewFareFamily } from 'features/FareFamilies/components/NewFareFamily'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { resolvers as loginResolvers } from 'resolvers/login'
import { canWrite } from 'features/Users/utils'
import { sanitizePrice } from 'utils/math'
import { useLogin } from 'features/Login/hooks'
import { GET_SEGMENTS_SUGGESTIONS } from 'features/Segments/queries-legacy'
import { GTSegmentSuggestionsResponse } from 'gql/types.generated'
import { MAX_INT_FOR_PAGINATION_SIZE } from 'config/constants'

const messages = defineMessages({
  helmet: {
    id: 'fareFamilies.new.helmet',
    defaultMessage: 'New Fare Family | Nexbus Admin'
  }
})

const CREATE_FARE_FAMILIES = gql`
  mutation CreateFareFamilies($fareFamilies: [fareFamilyInput!]!) {
    createFareFamilies(input: { fareFamilies: $fareFamilies })
  }
`

const NewFareFamilyView = (): TSFixMe => {
  const navigate = useNavigate()
  const intl = useIntl()
  const client = useApolloClient()
  client.addResolvers(loginResolvers)

  const login = useLogin()
  const [getSegments, { loading: segmentsLoading, error: segmentsError }] = useLazyQuery<{
    segmentSuggestions: GTSegmentSuggestionsResponse
  }>(GET_SEGMENTS_SUGGESTIONS)

  useEffect((): TSFixMe => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  const [createFareFamily, { loading: createFareFamiliesLoading, error: createFareFamiliesError }] = useMutation(
    CREATE_FARE_FAMILIES,
    {
      onCompleted() {
        navigate('/fare-families')
      }
    }
  )

  if (!canWrite(FEATURES.FARE_FAMILY, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.FARE_FAMILY} />
  }

  const handleSubmit = async (values: TSFixMe): Promise<void> => {
    try {
      const { data: segmentsData } = await getSegments({
        variables: {
          input: {
            ids: values.segments.map((segment: TSFixMe) => segment.id),
            companyID: values.company.id,
            onlyNoFareFamily: true,
            includeReverse: true
          },
          pagination: {
            page: 1,
            size: MAX_INT_FOR_PAGINATION_SIZE
          }
        }
      })
      const segments = segmentsData?.segmentSuggestions?.items || []
      const fares = values.fares.map((fare: TSFixMe): TSFixMe => {
        return {
          bookingClassID: fare.bookingClass.id,
          seasonType: fare.seasonType,
          type: fare.type,
          timeWindow: fare.timeWindow,
          dow: fare.dow,
          demand: fare.demand,
          salesChannel: fare.salesChannel,
          serviceType: fare.serviceType,
          basePrice: sanitizePrice(fare.basePrice * 100, true),
          isActive: fare.isActive
        }
      })

      const fareFamilies = segments.map((segment): TSFixMe => {
        return {
          companyID: values.company.id,
          segmentID: segment.id,
          familyName: values.familyName,
          fares
        }
      })

      createFareFamily({
        variables: {
          fareFamilies
        }
      })
    } catch (error) {
      // DO NOTHING
    }
  }

  const loading = segmentsLoading || createFareFamiliesLoading
  const error = segmentsError || createFareFamiliesError

  return (
    <div>
      <FormLayout.Header>
        <Heading.H3>
          <FormattedMessage id="fareFamilies.new.title" defaultMessage="Add fare family" />
        </Heading.H3>
      </FormLayout.Header>
      <FormLayout.Content>
        <NewFareFamily onSubmit={handleSubmit} loading={loading} error={error} canEditCompany />
      </FormLayout.Content>
    </div>
  )
}

export default withLastUrl(NewFareFamilyView)
